const DEFAULT_POLLING_INTERVAL_MS = 1000;

/**
 * Сразу вызывает operation и ждёт intervalMs мс после каждого результата,
 * для которого isComplete возвращает false.
 * По умолчанию intervalMs равен 1000 мс.
 * Ошибка прекращает опрос. cancel() отклоняет promise с AbortError и отправляет
 * сигнал отмены в operation; для остановки операции она должна учитывать сигнал.
 * Необязательный timeoutMs ограничивает общее время опроса, включая ожидание
 * завершения вызовов. Допустимые значения — от 0 до Number.MAX_SAFE_INTEGER мс.
 * Если timeoutMs не передан, ограничения по времени нет.
 * По истечении таймаута promise отклоняется с TimeoutError и отправляется сигнал
 * отмены. При timeoutMs = 0 операция не запускается.
 */
export const pollUntil = <T>(
  operation: (signal: AbortSignal) => Promise<T>,
  isComplete: (result: T) => boolean,
  intervalMs = DEFAULT_POLLING_INTERVAL_MS,
  timeoutMs?: number
): { promise: Promise<T>; cancel: () => void } => {
  if (
    !Number.isFinite(intervalMs) ||
    intervalMs < 0 ||
    intervalMs > Number.MAX_SAFE_INTEGER
  ) {
    throw new RangeError(
      'intervalMs must be between 0 and Number.MAX_SAFE_INTEGER'
    );
  }

  if (
    timeoutMs !== undefined &&
    (!Number.isFinite(timeoutMs) ||
      timeoutMs < 0 ||
      timeoutMs > Number.MAX_SAFE_INTEGER)
  ) {
    throw new RangeError(
      'timeoutMs must be between 0 and Number.MAX_SAFE_INTEGER'
    );
  }

  const controller = new AbortController();
  let cancel!: () => void;

  const promise = new Promise<T>((resolve, reject) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let deadlineId: ReturnType<typeof setTimeout> | undefined;

    const clearTimers = () => {
      clearTimeout(timeoutId);
      clearTimeout(deadlineId);
    };

    const abort = (error: DOMException) => {
      if (settled) return;

      settled = true;
      clearTimers();
      controller.abort(error);
      reject(error);
    };

    cancel = () =>
      abort(new DOMException('Polling was cancelled', 'AbortError'));

    const run = async () => {
      timeoutId = undefined;

      try {
        const result = await operation(controller.signal);
        if (settled) return;

        const complete = isComplete(result);
        if (settled) return;

        if (complete) {
          settled = true;
          clearTimers();
          resolve(result);
        } else {
          timeoutId = setTimeout(run, intervalMs);
        }
      } catch (error) {
        if (settled) return;

        settled = true;
        clearTimers();
        reject(error);
      }
    };

    if (timeoutMs !== undefined) {
      const expire = () =>
        abort(new DOMException('Polling timed out', 'TimeoutError'));

      if (timeoutMs === 0) {
        expire();
        return;
      }

      deadlineId = setTimeout(expire, timeoutMs);
    }

    void run();
  });

  return { promise, cancel };
};

pollUntil.isAbortError = (error: unknown) => {
  // @ts-expect-error ignore getting "name" of unknown
  return error?.name === 'AbortError';
};
